import { useEffect, useState } from "react";
import { Select, useToast } from "@salesapp/ui";
import { crmApi, type OwnedRecordKind, type OwnerOption } from "../../lib/crmApi";

/** The salesperson a record is assigned to, shown in the record's highlight row.
 *  Sales managers (org admin or CRM app admin) get a picker; everyone else sees the name. */
export function RecordOwner({
  orgId,
  kind,
  recordId,
  ownerUserId,
  ownerName,
  canReassign,
  onChanged,
}: {
  orgId: string;
  kind: OwnedRecordKind;
  recordId: string;
  ownerUserId?: string | null;
  ownerName?: string | null;
  canReassign?: boolean;
  onChanged: (owner: { owner_user_id: string; owner_name: string | null }) => void;
}) {
  const { show } = useToast();
  const [options, setOptions] = useState<OwnerOption[] | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!canReassign) return;
    let cancelled = false;
    crmApi
      .listOwners(orgId)
      .then((result) => {
        if (!cancelled) setOptions(result.items);
      })
      .catch(() => {
        if (!cancelled) setOptions([]);
      });
    return () => {
      cancelled = true;
    };
  }, [canReassign, orgId]);

  const name = ownerName || "Unassigned";
  if (!canReassign) return <span className="crm-record-owner-name">{name}</span>;

  const choices = [...(options ?? [])];
  if (ownerUserId && !choices.some((item) => item.user_id === ownerUserId)) {
    choices.unshift({ user_id: ownerUserId, display_name: name });
  }

  async function reassign(nextId: string) {
    if (!nextId || nextId === ownerUserId) return;
    setSaving(true);
    try {
      const result = await crmApi.reassignOwner(kind, recordId, nextId, orgId);
      onChanged({ owner_user_id: result.owner_user_id, owner_name: result.owner_name });
      show(`Salesperson changed to ${result.owner_name ?? "the new owner"}`, "success");
    } catch (err) {
      show(err instanceof Error ? err.message : "Could not change the salesperson", "danger");
    } finally {
      setSaving(false);
    }
  }

  return (
    <Select
      aria-label="Salesperson"
      style={{ padding: "var(--space-1) var(--space-2)", minHeight: "auto", fontSize: "var(--font-size-sm)" }}
      value={ownerUserId ?? ""}
      disabled={saving || options === null}
      onChange={(event) => void reassign(event.target.value)}
    >
      {ownerUserId ? null : <option value="">Unassigned</option>}
      {choices.map((item) => (
        <option key={item.user_id} value={item.user_id}>
          {item.display_name}
        </option>
      ))}
    </Select>
  );
}
